import { Dialog, Typography } from "@mui/material";
import { DoraButton, DoraTextInput } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import {
  selectCurrentOrderTemplate,
  selectOrderTemplatesForClientTeamAndSearch,
  selectTemplateBelongsToClient,
} from "../../../../ducks/data/client-order-templates/selectors";
import { useMemo, useState } from "react";
import {
  createClientCargoTemplate,
  updateClientCargoTemplate,
} from "../../../../ducks/data/client-order-templates";
import CreateShipmentData from "../CreateEditShipmentData";
import { mapFormStopToAPIFormat } from "../map-form-stop-to-api-format";
import { useConfirmDropDeletedExtraCo2Types } from "../../ConfirmDropDeletedExtraCo2Types";

const ClientCargoTemplateButtonsAndModal = (props: {
  clientId: string;
  teamId: string;
  // Read on click rather than passed as a watched value: watching the whole
  // form re-rendered the entire cargo dialog on every keystroke (DORA-406).
  getShipmentFormData: () => CreateShipmentData;
  createNewContext: boolean;
  cargoId: string | null;
  validateCargoData: (onSuccess: any) => () => any;
}) => {
  const {
    clientId,
    teamId,
    getShipmentFormData,
    createNewContext,
    cargoId,
    validateCargoData,
  } = props;
  const { t } = useTranslation(["orderTemplates"]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const dispatch = useAppDispatch();
  const selectedTemplate = useSelector(selectCurrentOrderTemplate);

  const onSaveNewTemplateClick = () => {
    validateCargoData(() => {
      setShowCreateDialog(true);
    })();
  };

  const {
    ask: askToDropDeletedExtraCo2Types,
    dialog: dropDeletedExtraCo2TypesDialog,
  } = useConfirmDropDeletedExtraCo2Types("SAVE");

  const templateData = () => {
    const shipmentFormData = getShipmentFormData();
    return {
      ...shipmentFormData,
      clientId,
      smartMatch: false,
      pickupList: shipmentFormData.pickupList.map(mapFormStopToAPIFormat),
      dropoffList: shipmentFormData.dropoffList.map(mapFormStopToAPIFormat),
      stopUnits: shipmentFormData.stopUnits,
      qualities: shipmentFormData.qualities.map((q) => q.id),
      products: shipmentFormData.products.map((p) => p.id),
      requirements: shipmentFormData.requirements.map((r) => r.id),

      // TODO big bug with io-ts handling, it all comes from the initial cargo io-ts
      minTemperature: shipmentFormData.minTemperature || (null as any),
      maxTemperature: shipmentFormData.maxTemperature || (null as any),
    };
  };

  // Resends once without the lines whose CO2 type was deleted, if the user
  // agrees; false when nothing was saved.
  const saveAllowingToDropDeletedExtraCo2Types = async (
    save: (dropDeletedExtraCo2Types: boolean) => Promise<object>
  ) => {
    const result = await save(false);
    if (!("refused" in result)) {
      return true;
    }
    if (!(await askToDropDeletedExtraCo2Types())) {
      return false;
    }
    return !("refused" in (await save(true)));
  };

  const onSaveClick = async (name: string) => {
    const data = templateData();
    setRequestInProgress(true);
    await saveAllowingToDropDeletedExtraCo2Types((dropDeletedExtraCo2Types) =>
      dispatch(
        createClientCargoTemplate({
          clientId,
          name,
          cargoIdToCopyInvoiceLinesFrom: cargoId,
          cargoIdToCopyFilesFrom: cargoId,
          data,
          dropDeletedExtraCo2Types,
        })
      ).unwrap()
    )
      .then((saved) => {
        if (saved) {
          setShowCreateDialog(false);
        }
      })
      .catch(() => {
        // The createClientCargoTemplate thunk has already reported it and shown the error toast.
      })
      .finally(() => setRequestInProgress(false));
  };

  const onUpdateClick = async () => {
    const data = templateData();
    await saveAllowingToDropDeletedExtraCo2Types((dropDeletedExtraCo2Types) =>
      dispatch(
        updateClientCargoTemplate({
          clientId,
          templateId: selectedTemplate!.id,
          cargoId,
          data,
          dropDeletedExtraCo2Types,
        })
      ).unwrap()
    )
      .then((saved) => {
        if (saved) {
          setShowCreateDialog(false);
        }
      })
      .catch(() => {
        // The updateClientCargoTemplate thunk has already reported it and shown the error toast.
      });
  };

  const currentOrderTemplate = useSelector(selectCurrentOrderTemplate);

  const isUpdateTemplateEnabled = useSelector(
    selectTemplateBelongsToClient(currentOrderTemplate?.id, clientId)
  );

  return (
    <>
      {dropDeletedExtraCo2TypesDialog}
      {showCreateDialog && (
        <ClientCargoTemplatesSaveModal
          clientId={clientId}
          requestInProgress={requestInProgress}
          teamId={teamId}
          onSaveClick={onSaveClick}
          onCancelClick={() => setShowCreateDialog(false)}
        />
      )}
      {currentOrderTemplate && !createNewContext && (
        <div style={{ fontSize: 14, color: "var(--gray-500)" }}>
          {t("orderTemplates:currentTemplate")}: {currentOrderTemplate.name}
        </div>
      )}
      <DoraButton
        disabled={!clientId}
        onClick={onSaveNewTemplateClick}
        variant="primary-filled"
      >
        {t("orderTemplates:saveNewTemplate")}
      </DoraButton>
      {currentOrderTemplate && (
        <DoraButton
          variant="primary"
          onClick={onUpdateClick}
          disabled={!isUpdateTemplateEnabled}
        >
          {t("orderTemplates:updateSelectedTemplate")}
        </DoraButton>
      )}
    </>
  );
};

const ClientCargoTemplatesSaveModal = ({
  clientId,
  teamId,
  requestInProgress,
  onSaveClick,
  onCancelClick,
}: {
  clientId: string;
  teamId: string;
  requestInProgress: boolean;
  onSaveClick: (name: string) => void;
  onCancelClick: () => void;
}) => {
  const { t } = useTranslation(["components", "orderTemplates"]);
  const [name, setName] = useState("");
  const clientTemplates = useSelector(
    selectOrderTemplatesForClientTeamAndSearch({
      clientId,
      teamId,
      search: "",
    })
  );

  const nameExists = useMemo(
    () => !!clientTemplates.find((x) => x.name === name),
    [clientTemplates, name]
  );

  return (
    <Dialog open onClose={onCancelClick}>
      <form
        className="p-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSaveClick(name);
        }}
      >
        <Typography variant="h2">
          {t("orderTemplates:saveModal.title")}
        </Typography>
        <Typography variant="body1">
          {t("orderTemplates:saveModal.text")}
        </Typography>
        <br />
        <DoraTextInput
          value={name}
          onChange={(e) => setName(e)}
          placeholder={t("orderTemplates:saveModal.nameNewPlaceholder")}
          autoFocus
        />
        {nameExists && (
          <div className="text-red-500 mt-2">
            {t("orderTemplates:saveModal.nameExists")}
          </div>
        )}
        <div className="mt-2 flex gap-2 items-center justify-end">
          <DoraButton variant="primary" onClick={onCancelClick}>
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            type="submit"
            disabled={!name || nameExists || requestInProgress}
          >
            {t("components:buttonLabels.save")}
          </DoraButton>
        </div>
      </form>
    </Dialog>
  );
};

export default ClientCargoTemplateButtonsAndModal;
